import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { brand } from '../../config/brand';
import packageJson from '../../../package.json';
const { version } = packageJson;

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  isDark: boolean;
  toggleTheme: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose, isDark, toggleTheme }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAdmin } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);

  const baseMenuItems = [
    { path: '/dashboard', label: 'Dashboard', icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ) },
    { path: '/buildings', label: 'Gebäude', icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>
    ) },
    { path: '/activity', label: 'Aktivitäten', icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M22 12h-4l-3 9L9 3l-3 9H2" />
      </svg>
    ) },
  ];

  const adminMenuItems = [
    { path: '/admin/mqtt', label: 'MQTT', icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
      </svg>
    ) },
    { path: '/users', label: 'Benutzer', icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
    ) },
  ];

  const menuItems = isAdmin() ? [...baseMenuItems, ...adminMenuItems] : baseMenuItems;

  const isActive = (path: string) => {
    if (path === '/buildings') return location.pathname.startsWith('/buildings');
    return location.pathname === path;
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <>
      {/* Mobile drawer overlay */}
      <div 
        className={`lg:hidden fixed inset-0 bg-slate-950/20 dark:bg-slate-950/60 backdrop-blur-sm z-40 transition-opacity duration-300 ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`} 
        onClick={onClose} 
      />

      <aside className={`
        fixed lg:static inset-y-0 left-0 w-72 lg:w-80 z-50 
        glass-card !rounded-none lg:!rounded-3xl lg:m-6 
        flex flex-col transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="px-6 pt-8 pb-4 flex flex-col items-center">
          {brand.logo && (
            <img src={brand.logo} alt="" className="w-48 object-contain" />
          )}
          <button className="lg:hidden absolute top-4 right-4 p-2 dark:text-white" onClick={onClose}>
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <nav className="flex-1 px-4 py-2 space-y-1">
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={onClose}
              className={`
                flex items-center gap-4 px-4 py-3.5 rounded-2xl transition-all duration-200 group
                ${isActive(item.path)
                  ? 'bg-[#C7E4FF]/40 text-[#216dab] dark:bg-[#216dab]/20 dark:text-[#3aa6e9] font-semibold'
                  : 'text-slate-600 dark:text-slate-400 hover:text-[#216dab] dark:hover:text-slate-200 hover:bg-[#C7E4FF]/20 dark:hover:bg-white/5'}
              `}
            >
              <span className={`transition-transform duration-300 group-hover:scale-110 ${isActive(item.path) ? 'text-[#216dab] dark:text-[#3aa6e9]' : 'text-slate-400 dark:text-slate-500 group-hover:text-[#216dab]'}`}>
                {item.icon}
              </span>
              <span className="font-medium text-base">{item.label}</span>
            </Link>
          ))}
        </nav>

        <div className="p-6 mt-auto space-y-3">
          {/* Dark / Light mode toggle */}
          <button
            onClick={toggleTheme}
            title={isDark ? 'Zum Light Mode wechseln' : 'Zum Dark Mode wechseln'}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl bg-black/5 dark:bg-white/5 hover:bg-black/10 dark:hover:bg-white/10 transition-all group"
          >
            {isDark ? (
              <svg className="w-5 h-5 text-amber-400 group-hover:rotate-12 transition-transform" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg className="w-5 h-5 text-[#216dab] group-hover:-rotate-12 transition-transform" fill="currentColor" viewBox="0 0 20 20">
                <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
              </svg>
            )}
            <span className="text-sm font-bold text-slate-600 dark:text-slate-400">
              {isDark ? 'Light Mode' : 'Dark Mode'}
            </span>
          </button>

          <div className="glass-card p-4 !bg-black/5 dark:!bg-white/5 !border-transparent relative overflow-hidden group">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => setShowUserMenu(!showUserMenu)}>
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#216dab] to-[#93c94f] flex items-center justify-center font-bold text-white shadow-lg">
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-base font-semibold text-slate-800 dark:text-slate-200 truncate">{user?.firstName} {user?.lastName}</p>
                <p className="text-sm font-normal text-slate-400">{user?.role}</p>
              </div>
              <svg className={`w-4 h-4 text-slate-400 transition-transform duration-300 ${showUserMenu ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
              </svg>
            </div>

            {showUserMenu && (
              <div className="mt-4 space-y-1 animate-in slide-in-from-bottom-2">
                {user?.authSource === 'WISO' ? (
                  <span className="flex items-center gap-3 w-full px-3 py-2 text-sm text-slate-400 dark:text-slate-500">
                    Passwort im WiSo Portal ändern
                  </span>
                ) : (
                  <Link
                    to="/change-password"
                    className="flex items-center gap-3 w-full px-3 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 rounded-lg hover:bg-white/50 dark:hover:bg-white/10"
                    onClick={() => setShowUserMenu(false)}
                  >
                    Passwort ändern
                  </Link>
                )}
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-3 w-full px-3 py-2 text-sm font-medium text-rose-500 rounded-lg hover:bg-rose-500/10"
                >
                  Abmelden
                </button>
              </div>
            )}
          </div>
          <p className="text-center text-sm text-slate-400 dark:text-slate-600 font-mono">
            v{version}
          </p>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
